// =============================================================================
// i18n SETUP — Internationalization Configuration
// =============================================================================
// This file configures react-i18next for the restaurant website.
//
// HOW react-i18next WORKS:
// 1. You define translation keys and their values in JSON files
//    (one JSON file per language: en.json, fr.json, es.json, etc.)
//
// 2. In your React components, instead of writing:
//      <h1>Our Menu</h1>
//    You write:
//      <h1>{t('menu.heading')}</h1>
//    where t() looks up the key in the current language's JSON file.
//
// 3. When the user switches language, ALL t() calls re-render with
//    the new language's values — no page reload needed.
//
// NAMESPACE SEPARATION:
// We use two "namespaces" (groups) of translations:
// - "ui": Framework UI strings (button labels, form placeholders, etc.)
//         Provided by the framework, same across all restaurant sites.
// - "content": Restaurant-specific content (taglines, descriptions, etc.)
//              Provided by the restaurant owner per language.
//
// FILE STRUCTURE:
//   packages/core/src/i18n/locales/en/ui.json   ← Framework strings
//   apps/site/content/i18n/en/content.json      ← Restaurant content
// =============================================================================

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * Initialize i18next with the given configuration.
 *
 * @param defaultLocale - The fallback language (e.g., "en")
 * @param supportedLocales - All available languages (e.g., ["en", "fr"])
 * @param uiTranslations - Framework UI translations (keyed by locale)
 * @param contentTranslations - Restaurant content translations (keyed by locale)
 *
 * USAGE in the site app:
 *   import { initI18n } from '@webzite/core/i18n/setup';
 *   import enUI from './locales/en/ui.json';
 *   import frUI from './locales/fr/ui.json';
 *   import enContent from './content/i18n/en/content.json';
 *   import frContent from './content/i18n/fr/content.json';
 *
 *   initI18n('en', ['en', 'fr'], { en: enUI, fr: frUI }, { en: enContent, fr: frContent });
 */
export function initI18n(
  defaultLocale: string,
  supportedLocales: string[],
  uiTranslations: Record<string, Record<string, string>>,
  contentTranslations: Record<string, Record<string, string>>,
) {
  // Build the resources object that i18next expects
  // Structure: { en: { ui: {...}, content: {...} }, fr: { ui: {...}, content: {...} } }
  const resources: Record<string, Record<string, Record<string, string>>> = {};

  for (const locale of supportedLocales) {
    resources[locale] = {
      ui: uiTranslations[locale] ?? {},
      content: contentTranslations[locale] ?? {},
    };
  }

  void i18n
    // Detect user language from browser settings / URL / localStorage
    .use(LanguageDetector)
    // Connect to React (provides the useTranslation hook)
    .use(initReactI18next)
    // Initialize
    .init({
      // Available translations
      resources,

      // Default language (used when detection fails)
      fallbackLng: defaultLocale,

      // Only allow these languages (ignore unsupported browser languages)
      supportedLngs: supportedLocales,

      // Default namespace — "ui" is used when you call t('key')
      // fallbackNS allows t('key') to search both 'ui' and 'content' namespaces seamlessly
      defaultNS: 'ui',
      fallbackNS: 'content',

      // Namespaces to load
      ns: ['ui', 'content'],

      // Don't escape HTML entities (React handles XSS protection)
      interpolation: {
        escapeValue: false,
      },

      // Only load language code (e.g., 'en' instead of 'en-US')
      load: 'languageOnly',

      // Allow matching language variants like 'fr-FR' -> 'fr' against supportedLngs
      nonExplicitSupportedLngs: true,

      // Language detection configuration
      detection: {
        // Order of detection methods:
        // 1. localStorage: remembers user's explicit choice
        // 2. navigator: user's native browser language on first visit
        // 3. path: /fr, /en, etc.
        // Fallback: defaultLocale (e.g. fr)
        order: ['localStorage', 'navigator', 'path'],

        // How to detect language from the URL path
        lookupFromPathIndex: 0,

        // Cache the user's choice in localStorage
        caches: ['localStorage'],
      },

      // React-specific settings
      react: {
        // Use Suspense for lazy-loaded translations
        useSuspense: true,
      },
    });

  return i18n;
}

// Re-export for convenience
export { i18n };
export { useTranslation } from 'react-i18next';
